import React, { useCallback } from "react";
import { useMemo } from "react";
import { useEffect } from "react";

// API
import api from "../../../api/api";

// Utils
import useType from "../../../utils/hooks/useType";

// Components
import { CancelButton } from "../../Controls/CancelButton";
import { ConfirmButton } from "../../Controls/ConfirmButton";
import { SearchModalInput } from "../../Controls/SearchModalInput";
import StackStyle from "../../Styled/Stack.style";
import QueueStyle from "../../Styled/Queue.style";

// Effects
import LoadEffect from "../../../effects/LoadEffect";
import hasNoValue from "../../../utils/helpers/hasNoValue";
import TableRadioSelect from "../../Table/TableRadioSelect";
import useAsync from "../../../utils/hooks/useAsync";
import { MMCodeInput } from "../../Controls/MMCodeInput";
import Button from "../../Controls/Button";

export default function SearchModal({
  searchFor,
  newDemoUsed,
  value,
  setValue,
  setResultItem,
  setShowSearchModal,
  validate,
  showMMCodeModalAuto,
  setShowMMCodeModalAuto,
  setHasNoMMCodeResult,
  addToUpdateHistory,
}) {
  const [
    startSearch,
    searchResult,
    isSearching,
    searchResultError,
    setSearchResult,
  ] = useAsync(
    useCallback(
      () =>
        searchFor === "MMCode"
          ? api.searchMMCode(value, newDemoUsed)
          : searchFor === "VIN" && api.searchVIN(value),
      [newDemoUsed, searchFor, value]
    ),
    "",
    `Issue searching for ${searchFor}.`
  );
  const [resultItemID, setResultItemID] = useType("string", "");
  const [isSearchActive, setIsSearchActive] = useType("boolean", false);
  const [isSearchInputFocused, setIsSearchInputFocused] = useType(
    "boolean",
    false
  );

  useEffect(() => {
    if(!hasNoValue(value)) {
      setIsSearchActive(true);
    }
  }, []);

  // Search MMCode
  useEffect(() => {
    if (value.length > 3 && isSearchActive) {
      startSearch(true);
    }

    return () => setIsSearchActive(false);
  }, [value, isSearchActive, startSearch, setIsSearchActive]);
  

  // Table headers
  const headers = useMemo(
    () =>
      Object.keys(searchResult?.length > 0 ? searchResult[0] : [])?.map(
        (key) => ({
          id: key,
          label: key.replace(/[_]/g, " "),
        })
      ),
    [searchResult]
  );
  // Table rows
  const rows = useMemo(() => {
    if (searchResult?.length === 0) return [];
    return searchResult;
  }, [searchResult]);

  // Selected item
  const resultItem = useMemo(
    () => searchResult?.find((sr) => sr.model_ID === resultItemID),
    [searchResult, resultItemID]
  );

  const dynamicID = searchFor === "MMCode" ? "crM_ID" : "vinNumber";

  useEffect(() => {
    if(!showMMCodeModalAuto) return;
    if(hasNoValue(rows)) return;
    console.log("🚀 ~ SearchModal ~ rows:", rows);

    const item = rows?.find((r) => r?.status?.toLowerCase() === "active");
    console.log("🚀 ~ SearchModal ~ item:", item);
    if(!item) {
      setHasNoMMCodeResult(true);
      return;
    }

    setResultItem(item);
    setShowSearchModal(false);
    setShowMMCodeModalAuto(false);
  }, [showMMCodeModalAuto, rows, setHasNoMMCodeResult, setResultItem, setShowSearchModal, setShowMMCodeModalAuto, dynamicID, resultItemID]);

  // Enter key event listener
  useEffect(() => {
    const listener = (event) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        event.preventDefault();
        if (isSearchInputFocused) setIsSearchActive(true);
        else if (resultItemID) {
          setResultItem(resultItem);
          setShowSearchModal(false);
          setShowMMCodeModalAuto(false);
        }
      }
    };
    document.addEventListener("keydown", listener);

    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [
    setIsSearchActive,
    isSearchInputFocused,
    resultItem,
    resultItemID,
    setResultItem,
    setShowSearchModal,
    setShowMMCodeModalAuto,
  ]);

  console.log('searchResult', searchResult);
  
  return (
    <StackStyle padding="8px 0" gap="20px">
      {(showMMCodeModalAuto && isSearching) ? (
        <p>Fetching info automatically</p>
      ) : (
        <>
        <SearchModalInput
          value={value}
          setValue={setValue}
          setIsSearchActive={setIsSearchActive}
          onFocus={setIsSearchInputFocused}
          validate={validate}
        />
        <LoadEffect condition={!isSearching}>
          {(!searchResultError && searchResult?.length > 0) ? (
            <TableRadioSelect
              headers={headers}
              rows={rows}
              id={dynamicID}
              resultItemID={resultItemID}
              setResultItemID={setResultItemID}
            />
          ) : (!searchResultError && (searchResult?.length === 0 || !searchResult)) ? (
            <p>No results found...</p>
          ) : (searchResultError) && (
            <p>Error searching for MMCodes</p>
          )}
        </LoadEffect>
        <QueueStyle justifyContent="center">
          
          {(!hasNoValue(searchResult)) ? (
            <>
              <CancelButton onClick={() => {
                setShowSearchModal(false)
                setShowMMCodeModalAuto(false);  
                if(hasNoValue(searchResult) && hasNoValue(resultItem)) setHasNoMMCodeResult(true);
              }} />
              <ConfirmButton
                onClick={() => {
                  const item = rows.find((r) => r[dynamicID] === resultItemID);
                  setResultItem(item);
                  setShowSearchModal(false);
                  setShowMMCodeModalAuto(false);  
                }}
                disabled={hasNoValue(resultItemID)}
              />
            </>
          ) : ((hasNoValue(searchResult) && hasNoValue(resultItem)) && !isSearching) && (
            <Button
              type="primary"
              label={"Continue manually"} 
              size="small"
              loading={false}
              stretch={false}
              center={true}
              onClick={() => {
                setShowSearchModal(false);
                setShowMMCodeModalAuto(false); 
                setHasNoMMCodeResult(true);
                addToUpdateHistory("info", "Continued manually", "", "");
              }}
            />
          )}
        </QueueStyle>
        </>
      )}
      
    </StackStyle>
  );
}
